import { soundManager } from "../../SoundManager";
import { SOUND_NAME, MUSIC_FADE_MS } from "../../sounds";
import { HowlCache } from "../../State/Slices/soundsSlice";

/**
 * The music the free-spins OUTRO popups play — TOTAL WIN and MAX WIN.
 *
 * ── One rule, both popups ──
 *
 * They are the same dialog for different endings: the same `win_celebration`
 * spine, the same slots, the same tap word, the same wipe out of the room. So
 * they sound the same. Vladimir, 2026-09-23: *"they are essentially coming from
 * same spine and are in general same popups just different purposes... run it on
 * loop untill closed and thats it. same way both."*
 *
 * The track is `win_music`, the celebration's own loop. Nothing else can be
 * using it at that moment:
 *
 *   TOTAL WIN   the last spin's celebration may have been playing it, but it
 *               has faded out by the time this opens — there is a pause between
 *               the two. Restarting is fine and Vladimir asked for it plainly:
 *               *"i dont need without restarting, restart it, we have a pause in
 *               between anyway dont bother."*
 *   MAX WIN     a capped round suppresses the big-win celebration entirely (see
 *               isAnimatedWin in useFreeSpinBasicWinController), so the track is
 *               free either way.
 *
 * It also replaces what was there before, which was nothing: both popups used to
 * reach for a `total_win_loop` / `max_win_loop` that has no file in this game,
 * so the room went quiet and the popup sat in SILENCE for up to 30 seconds.
 *
 * ── Why the room's track is not restored on the way out ──
 *
 * The TRANSITION owns that handover: the wipe fades the feature track down as it
 * starts and fades the base track in when it completes. Restoring it here would
 * raise a track the wipe is about to lower — two fades over one handover, which
 * is the fault the total win popup's own comment already warns about.
 */

/**
 * Whichever room track might be up. Ordered so the feature's own pair is found
 * before the base game's — the same list the win celebration's
 * pauseBackgroundMusic walks, for the same reason: it does not matter which room
 * we are in, only which track is actually playing.
 */
const ROOM_TRACKS = [
  SOUND_NAME.hold_and_win_music,
  SOUND_NAME.feature_background_music,
  SOUND_NAME.feature_ambient_music,
  SOUND_NAME.free_spin_background_music,
  SOUND_NAME.background_music,
];

/**
 * Duck the room and start the celebration's loop.
 *
 * @param {number} [fadeMs]
 */
// `track`: the popup's loop — TOTAL WIN has its own (total_win_music,
// 2026-10-22), MAX WIN keeps win_music.
export const startOutroWinMusic = (fadeMs = MUSIC_FADE_MS, track = SOUND_NAME.win_music) => {
  const room = ROOM_TRACKS.find((name) => name && HowlCache[name]?.playing());
  if (room) soundManager.fadeOut(room, fadeMs);

  // Set on the Howl rather than trusted from the registry: `win_music` is
  // registered `loop: true`, but a one-shot playback elsewhere can clear it and
  // a popup that stops after one pass is the bug this exists to prevent.
  HowlCache[track]?.loop(true);

  // STOPPED first, and that is what makes this a restart. `fadeIn` ends with
  // `sound.play()`, which on a Howl that is still playing spawns a SECOND sound
  // id — the same music layered over itself rather than begun again. Stopping
  // costs nothing when it is already silent, which is the normal case.
  soundManager.stop(track);
  soundManager.fadeIn(track, fadeMs);
};

/**
 * Let it go. Called from the popup's OUT clip, so the music leaves with the art.
 *
 * @param {number} [fadeMs]
 */
export const stopOutroWinMusic = (fadeMs = MUSIC_FADE_MS, track = SOUND_NAME.win_music) => {
  soundManager.fadeOut(track, fadeMs);
};
